import { CommonModule, DatePipe, NgIf } from '@angular/common'
import { Component, Input, OnChanges } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { NgIcon, provideIcons } from '@ng-icons/core'
import { heroCheckCircle, heroPencil, heroTrash, heroXMark } from '@ng-icons/heroicons/outline'
import { ButtonComponent } from '@progress/kendo-angular-buttons'
import { DateInputCustomFormatPlaceholder, DatePickerComponent } from '@progress/kendo-angular-dateinputs'
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns'
import { AddCommandDirective, CancelCommandDirective, CellTemplateDirective, ColumnComponent, CommandColumnComponent, DataBindingDirective, EditCommandDirective, EditTemplateDirective, FocusableDirective, GridComponent, GridToolbarFocusableDirective, RemoveCommandDirective, SaveCommandDirective, ToolbarTemplateDirective } from '@progress/kendo-angular-grid'
import { TextAreaDirective, TextBoxDirective } from '@progress/kendo-angular-inputs'
import { ClientServices } from '../../../../@theme/services/clients.service'
import { TypesServices } from '../../../../@theme/services/typeslist.service'
import { Solicitor } from '../../../../models/repEditorModels'
import { Utilities } from '../../../../utilities/utilities'
import { GridEditorBaseComponent } from '../../shared/rep-editor-base/grid-editor-base-component'
import { RepEditorStateService } from '../../shared/rep-editor-state.service'
import { AdminOnlyDirective } from '../shared/admin-only.directive'

@Component({
    selector: 'gd-rep-editor-solicitor',
    templateUrl: './rep-editor-solicitor.component.html',
    styleUrls: ['./rep-editor-solicitor.component.scss'],
    imports: [
        CommonModule,
        GridComponent,
        DataBindingDirective,
        ToolbarTemplateDirective,
        GridToolbarFocusableDirective,
        AddCommandDirective,
        ButtonComponent,
        ColumnComponent,
        EditTemplateDirective,
        DropDownListComponent,
        FormsModule,
        ReactiveFormsModule,
        TextBoxDirective,
        TextAreaDirective,
        CellTemplateDirective,
        DatePickerComponent,
        CommandColumnComponent,
        FocusableDirective,
        EditCommandDirective,
        NgIf,
        RemoveCommandDirective,
        SaveCommandDirective,
        CancelCommandDirective,
        DatePipe,
        NgIcon,
        AdminOnlyDirective
    ],
    providers: [
        provideIcons({ heroPencil, heroTrash, heroCheckCircle, heroXMark })
    ]
})
export class RepEditorSolicitorComponent
  extends GridEditorBaseComponent
  implements OnChanges
{
  @Input() override repId: number = 0
  solicitor: Solicitor[]
  firmTypes: any[]
  activeFirmTypes = this.repState.getActiveFirmTypeList()
  public asDateInput = (value) =>
    Utilities.asType<DateInputCustomFormatPlaceholder>(value)

  constructor(
    typesService: TypesServices,
    clientService: ClientServices,
    formBuilder: UntypedFormBuilder,
    repEditorStateService: RepEditorStateService,
  ) {
    super(typesService, clientService, formBuilder, repEditorStateService)
  }

  ngOnChanges() {
    this.checkGridState()

    if (!this.repState.repSolicitors) {
      this.initGrid()
    }

    if (!this.repState.firmTypeList) {
      this.typesService.getFirmTypes().subscribe((response) => {
        this.repState.firmTypeList = response
      })
    }
  }

  initGrid() {
    this.clientService
      .getSolicitorForRep({ repId: this.repId })
      .subscribe((response) => {
        this.repState.repSolicitors = response.map((s) => new Solicitor(s))
      })
  }

  public addHandler({ sender }) {
    const newSolicitor = new Solicitor(null)
    newSolicitor.repId = this.repId
    newSolicitor.active = true
    this.addHandlerShared({ sender }, newSolicitor)
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }): void {
    const solicitor = new Solicitor(formGroup.value)
    this.clientService.submitSolicitorUpdate(solicitor).subscribe(() => {
      sender.closeRow(rowIndex)
      this.initGrid()
    })
  }

  public removeHandler({ dataItem }) {
    const itemToRemove = new Solicitor(dataItem)
    itemToRemove.active = false
    this.clientService.submitSolicitorUpdate(itemToRemove).subscribe(() => {
      this.initGrid()
    })
  }

  public createFormGroup(solicitor: Solicitor): UntypedFormGroup {
    return this.formBuilder.group({
      solicitorId: solicitor.solicitorId,
      firmTypeLookup: [solicitor.firmTypeLookup, Validators.required],
      firmType: solicitor.firmType,
      repId: solicitor.repId,
      solicitorDescription: [
        solicitor.solicitorDescription,
        Validators.required,
      ],
      startDate: solicitor.startDate,
      endDate: solicitor.endDate,
      fileLocation: solicitor.fileLocation,
      active: solicitor.active,
    })
  }
}
